import React, { useState, useEffect, useRef } from 'react';
import { exportCSV, exportJSON, uid, paidTotal, tuitionPaid } from '../db';
import {
  toast, Badge, Btn, Card, CardHead, Modal, ModalFooter,
  Field, Input, Select, Grid, Span, SecLabel, Search, Avatar, Stat,
  Tabs, NoData, TblWrap,
} from '../ui.jsx';
import { buildCard, printCard } from '../print.js';
import { fmtDob } from '../utils/fmtDob';

function Fees({ db, save }) {
  // ── Smart overdue logic ──────────────────────────────────────────
  const now = new Date();
  const ayStart = now.getMonth() >= 3
    ? new Date(now.getFullYear(), 3, 1)
    : new Date(now.getFullYear() - 1, 3, 1);
  const msPerMonth = 1000 * 60 * 60 * 24 * 30.44;
  const monthsElapsed = Math.max(1, Math.floor((now - ayStart) / msPerMonth));

  const classFeeMap = db.settings?.classFees || {};

  // Per-student computed rows — fee comes ONLY from class fee structure
  const feeRows = db.students.map(s => {
    const cf = classFeeMap[s.cls] || {};
    const monthly = parseFloat(cf.monthly) || 0;
    const examFee = parseFloat(cf.exam) || 0;
    // Only use class fee structure — ignore old s.fee / s.mf
    const annualFee = monthly > 0 ? monthly * 12 + examFee : 0;
    const paid = tuitionPaid(db.pays, s.id);
    // Total concessions given to this student
    const totalConcession = db.pays.filter(p => p.sid === s.id && p.concession).reduce((sum, p) => sum + (p.concession || 0), 0);
    const effectiveAnnualFee = Math.max(0, annualFee - totalConcession);
    const payMode = s.payMode || 'monthly';

    let expectedPaid = 0;
    let overdue = false;
    let overdueAmt = 0;
    let overdueMonths = 0;

    if (effectiveAnnualFee > 0) {
      if (payMode === 'annual') {
        expectedPaid = effectiveAnnualFee;
        overdue = paid < effectiveAnnualFee;
        overdueAmt = overdue ? effectiveAnnualFee - paid : 0;
      } else {
        expectedPaid = Math.min(effectiveAnnualFee, monthsElapsed * monthly);
        overdue = paid < expectedPaid;
        overdueAmt = overdue ? Math.round(expectedPaid - paid) : 0;
        overdueMonths = overdue && monthly > 0 ? Math.ceil((expectedPaid - paid) / monthly) : 0;
      }
    }

    const fullyPaid = effectiveAnnualFee > 0 && paid >= effectiveAnnualFee;
    const autoFst = effectiveAnnualFee === 0 ? 'N/A' : fullyPaid ? 'Paid' : overdue ? 'Overdue' : 'Pending';

    // Cumulative outstanding balance: sum all balanceRemaining from all transactions
    const studentPays = db.pays.filter(p => p.sid === s.id);
    const totalOutstandingBalance = studentPays.reduce((sum, p) => sum + (p.balanceRemaining || 0), 0);

    return {
      ...s, fee: effectiveAnnualFee, mf: monthly, _paid: paid, _monthly: monthly,
      _annualFee: effectiveAnnualFee, _totalConcession: totalConcession,
      _expectedPaid: expectedPaid, _overdue: overdue, _overdueAmt: overdueAmt,
      _overdueMonths: overdueMonths, _fullyPaid: fullyPaid, _autoFst: autoFst, _payMode: payMode,
      _previousBalance: totalOutstandingBalance
    };
  });

  const overdueRows = feeRows.filter(s => s._overdue && !s._fullyPaid);
  const totalCollected = feeRows.reduce((s, r) => s + r._paid, 0);
  const totalFee = feeRows.reduce((s, r) => s + r.fee, 0);
  const totalPending = overdueRows.reduce((s, r) => s + r._overdueAmt, 0);
  const collectionRate = totalFee > 0 ? Math.round(totalCollected / totalFee * 100) : 0;

  // ── Collect Fee state ────────────────────────────────────────────
  const [selClass, setSelClass] = useState('');
  const [selStu, setSelStu] = useState('');
  const [pyAmt, setPyAmt] = useState('');
  const [pyDt, setPyDt] = useState(new Date().toISOString().split('T')[0]);
  const [pyMd, setPyMd] = useState('Cash');
  const [pyMn, setPyMn] = useState(''); // kept for annual; monthly uses pyMns
  const [pyMns, setPyMns] = useState([]); // selected months array (monthly mode)
  const [pyTuitionPartial, setPyTuitionPartial] = useState(false); // partial tuition toggle
  const [pyTuitionPartialAmt, setPyTuitionPartialAmt] = useState(''); // partial amount
  const [pyExamFee, setPyExamFee] = useState(false); // include exam fee in this payment
  const [pyRef, setPyRef] = useState('');
  const [pyTy, setPyTy] = useState('Monthly Tuition');
  const [pyNote, setPyNote] = useState('');
  const [pyExtras, setPyExtras] = useState([]); // selected fextras indices
  const [pyExtraPartial, setPyExtraPartial] = useState({}); // { index: payingNowAmt string }
  const [pyExtrasConcession, setPyExtrasConcession] = useState(''); // concession on other charges
  const [pyNewExLabel, setPyNewExLabel] = useState('');
  const [pyNewExAmt, setPyNewExAmt] = useState('');
  const [editingExtraIdx, setEditingExtraIdx] = useState(null); // index of charge being edited inline
  const [editingExtraLabel, setEditingExtraLabel] = useState('');
  const [editingExtraAmt, setEditingExtraAmt] = useState('');
  const [pyConcession, setPyConcession] = useState(''); // concession on tuition
  const [pyIncludeBalance, setPyIncludeBalance] = useState(false); // include previous balance
  const [receipt, setReceipt] = useState(null);
  const [showRec, setShowRec] = useState(false);
  const [tab, setTab] = useState(0);
  const [ledStu, setLedStu] = useState('');
  const [ledCls, setLedCls] = useState('');
  const [histStu, setHistStu] = useState('');
  const [histCls, setHistCls] = useState('');
  const [overdueCls, setOverdueCls] = useState('');

  // ── Class Fee Structure ──────────────────────────────────────────
  const saveClassFee = (cls, field, val) => {
    const updated = { ...classFeeMap, [cls]: { ...(classFeeMap[cls] || {}), [field]: parseFloat(val) || 0 } };
    save({ ...db, settings: { ...db.settings, classFees: updated } });
  };

  // Auto-fill amount when student selected
  const handleSelectStudent = (stuId) => {
    setSelStu(stuId);
    setPyExtras([]); setPyExtraPartial({}); setPyExtrasConcession(''); setPyNewExLabel(''); setPyNewExAmt(''); setPyMn(''); setPyMns([]); setPyTuitionPartial(false); setPyTuitionPartialAmt(''); setPyExamFee(false);
    setPyAmt(''); // Reset paying amount to empty
    setPyIncludeBalance(false); // Reset previous balance inclusion
    setEditingExtraIdx(null); // Reset inline edit state
    if (stuId) {
      const st = db.students.find(x => x.id === stuId);
      if (st) {
        const cf = classFeeMap[st.cls];
        const mode = st.payMode || 'monthly';
        // Removed auto-fill logic - let user enter their own paying amount
      }
    }
  };

  const classes = [...new Set(db.students.map(s => s.cls))].sort();
  const classStudents = selClass ? feeRows.filter(s => s.cls === selClass) : [];
  const piStu = feeRows.find(s => s.id === selStu);
  const piBal = piStu ? Math.max(0, piStu.fee - piStu._paid) : 0;
  const piPct = piStu && piStu.fee > 0 ? Math.min(100, Math.round(piStu._paid / piStu.fee * 100)) : 0;
  const extrasGross = (piStu?.fextras || []).reduce((s, e, i) => {
    if (!pyExtras.includes(i)) return s;
    const alreadyPaid = (piStu.paidExtras || {})[e.label] || 0;
    return s + Math.max(0, (parseFloat(e.amt) || 0) - alreadyPaid);
  }, 0);
  const extrasConcessionAmt = Math.min(parseFloat(pyExtrasConcession) || 0, extrasGross);
  const extrasAfterConcession = Math.max(0, extrasGross - extrasConcessionAmt);
  const extrasPayingNow = (piStu?.fextras || []).reduce((s, e, i) => {
    if (!pyExtras.includes(i)) return s;
    const alreadyPaid = (piStu.paidExtras || {})[e.label] || 0;
    const remainingAmt = Math.max(0, (parseFloat(e.amt) || 0) - alreadyPaid);
    const partial = pyExtraPartial[i];
    const paying = partial !== undefined ? (parseFloat(partial) || 0) : remainingAmt;
    return s + paying;
  }, 0);
  // Cap paying now to after-concession amount
  const extrasTotal = Math.min(extrasPayingNow, extrasAfterConcession);
  const extrasRemaining = Math.max(0, extrasAfterConcession - extrasTotal);

  // Exam fee
  const stuExamFee = parseFloat((classFeeMap[piStu?.cls] || {}).exam) || 0;
  const examFeePaidAlready = db.pays.filter(p => p.sid === selStu && p.examFee > 0).reduce((s, p) => s + (p.examFee || 0), 0);
  const examFeeRemaining = Math.max(0, stuExamFee - examFeePaidAlready);
  const examFeeAlreadyPaid = examFeeRemaining === 0 && stuExamFee > 0;
  const examFeeAmt = pyExamFee ? examFeeRemaining : 0;
  const concessionAmt = parseFloat(pyConcession) || 0;
  const pyGrossAmt = (parseFloat(pyAmt) || 0) + extrasTotal;
  const pyNetAmt = Math.max(0, pyGrossAmt - concessionAmt);
  // Only count tuition if month is selected (monthly) or annual mode
  const isAnnualMode = piStu?._payMode === 'annual';
  // Monthly: tuition = months × mf, or partial amount if partial toggle on
  const monthlyBase = isAnnualMode ? 0 : (pyMns.length * (piStu?.mf || 0));
  const hasTuition = isAnnualMode
    ? (parseFloat(pyAmt) || 0) > 0
    : pyMns.length > 0;
  const effectiveTuition = isAnnualMode
    ? (parseFloat(pyAmt) || 0)
    : hasTuition
      ? (pyTuitionPartial ? Math.min(parseFloat(pyTuitionPartialAmt) || 0, monthlyBase) : monthlyBase)
      : 0;
  const tuitionRemaining = isAnnualMode ? 0 : Math.max(0, monthlyBase - effectiveTuition);
  const prevBalanceTxns = selStu ? db.pays.filter(p => p.sid === selStu && (p.balanceRemaining || 0) > 0 && !p.isPrevBalancePay) : [];
  const totalPrevBalance = prevBalanceTxns.reduce((sum, p) => sum + (p.balanceRemaining || 0), 0);
  const prevBalance = pyIncludeBalance ? totalPrevBalance : 0;
  const pyGrossTotal = effectiveTuition + extrasTotal + examFeeAmt + prevBalance;
  const pyEffectiveNet = Math.max(0, pyGrossTotal - concessionAmt);

  // Months already paid by this student (from pays records)
  const ALL_MONTHS = ['April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December', 'January', 'February', 'March'];
  const paidMonths = new Set(selStu ? db.pays.filter(p => p.sid === selStu && p.mn).flatMap(p => p.mn.split(',').map(m => m.trim())) : []);
  const availableMonths = ALL_MONTHS.filter(m => !paidMonths.has(m));

  // Other charges defined on the student (fextras)
  const stuExtras = piStu?.fextras || [];
  const pyTotalAmt = pyEffectiveNet;
  const pyPreview = piStu && pyEffectiveNet > 0 && !isAnnualMode
    ? Math.max(0, piStu._overdueAmt - pyEffectiveNet)
    : null;

  // History tab computed
  const filteredPays = histStu ? [...db.pays].filter(p => p.sid === histStu).reverse() : [...db.pays].reverse();
  const histStudent = histStu ? db.students.find(x => x.id === histStu) : null;
  const histTotal = filteredPays.reduce((s, p) => s + p.amt, 0);
  const recPay = () => {
    if (!selStu || !pyDt) { toast('Student and date required', 'err'); return; }
    const isAnnual = piStu?._payMode === 'annual';
    if (!isAnnual && pyMns.length === 0 && pyExtras.length === 0 && !pyExamFee && !pyIncludeBalance) { toast('Select at least one month, charge, exam fee, or previous balance', 'err'); return; }
    if (isAnnual && !parseFloat(pyAmt) && pyExtras.length === 0 && !pyIncludeBalance) { toast('Enter amount to pay', 'err'); return; }
    const s = db.students.find(x => x.id === selStu); if (!s) return;
    const selectedCharges = (piStu?.fextras || []).reduce((acc, e, i) => {
      if (!pyExtras.includes(i)) return acc;
      const fullAmt = parseFloat(e.amt) || 0;
      const alreadyPaid = (s.paidExtras || {})[e.label] || 0;
      const remainingAmt = Math.max(0, fullAmt - alreadyPaid);
      const partial = pyExtraPartial[i];
      const payingNow = partial !== undefined ? Math.min(parseFloat(partial) || 0, remainingAmt) : remainingAmt;
      const stillOwed = remainingAmt - payingNow;
      return [...acc, { ...e, amt: String(payingNow), remaining: stillOwed > 0 ? stillOwed : undefined }];
    }, []);
    const tuition = isAnnual ? (parseFloat(pyAmt) || 0) : effectiveTuition;
    const annualFeeVal = piStu?._annualFee || 0;
    const total = Math.max(0, tuition + extrasTotal + examFeeAmt + prevBalance - concessionAmt);
    const payingNowAmt = parseFloat(pyAmt) || 0;
    // balanceRemaining = only the unpaid portion of THIS transaction (not other transactions)
    const currentTxnBalance = Math.max(0, total - payingNowAmt);
    const balanceRemaining = currentTxnBalance;
    if (total <= 0 && payingNowAmt <= 0) { toast('Select at least one charge', 'err'); return; }
    const pp = tuitionPaid(db.pays, selStu), bb = Math.max(0, annualFeeVal - pp), ba = Math.max(0, bb - tuition);
    const rc = 'RCP-' + new Date().getFullYear() + '-' + String(db.pays.length + 1).padStart(4, '0');
    const mnLabel = isAnnual ? 'Annual' : (pyMns.length > 0 ? pyMns.join(', ') : selectedCharges.length > 0 ? selectedCharges.map(e => e.label).join(', ') : pyIncludeBalance ? 'Previous Balance' : '');
    const txnType = isAnnual ? 'Annual Fee' : pyMns.length > 0 ? pyTy : pyIncludeBalance && selectedCharges.length === 0 && !pyExamFee ? 'Previous Balance' : pyTy;
    const pay = { id: 'PAY' + uid(), dt: pyDt, sid: selStu, nm: s.fn + ' ' + s.ln, cls: s.cls, fee: annualFeeVal, amt: payingNowAmt, totalAmt: total, tuition, extras: selectedCharges, md: pyMd, ref: pyRef, mn: mnLabel, ty: txnType, note: pyNote, rc, bb, ba, concession: concessionAmt > 0 ? concessionAmt : undefined, extrasConcession: extrasConcessionAmt > 0 ? extrasConcessionAmt : undefined, extrasRemaining: extrasRemaining > 0 ? extrasRemaining : undefined, tuitionRemaining: tuitionRemaining > 0 ? tuitionRemaining : undefined, examFee: examFeeAmt > 0 ? examFeeAmt : undefined, balanceRemaining: balanceRemaining > 0 ? balanceRemaining : 0, prevBalancePaid: prevBalance > 0 ? prevBalance : undefined, isPrevBalancePay: pyIncludeBalance && tuition === 0 && extrasTotal === 0 && examFeeAmt === 0 ? true : undefined };

    // Update student's paidExtras and remove fully-paid charges
    const updatedStudents = db.students.map(x => {
      if (x.id !== selStu) return x;
      const paidExtras = { ...(x.paidExtras || {}) };
      selectedCharges.forEach(ch => {
        paidExtras[ch.label] = (paidExtras[ch.label] || 0) + (parseFloat(ch.amt) || 0);
      });
      // Remove charges that are fully paid (paidExtras >= original amt)
      const newFextras = (x.fextras || []).filter(fe => {
        const paid = paidExtras[fe.label] || 0;
        return paid < (parseFloat(fe.amt) || 0);
      });
      const extrasSum = newFextras.reduce((sum, e) => sum + (parseFloat(e.amt) || 0), 0);
      return { ...x, fextras: newFextras, paidExtras, fee: (x.mf || 0) * 12 + extrasSum };
    });

    // When previous balance is included, reduce balanceRemaining on source transactions
    // by the amount actually paid toward them
    // Amount paid toward previous balance = min(payingNowAmt, prevBalance) after covering current charges
    const paidTowardPrevBalance = prevBalance > 0
      ? Math.max(0, Math.min(prevBalance, payingNowAmt - (total - prevBalance)))
      : 0;
    let amountToDeduct = paidTowardPrevBalance;
    const updatedPays = [...db.pays, pay].map(p => {
      if (amountToDeduct <= 0) return p;
      if (p.sid !== selStu || !p.balanceRemaining || p.balanceRemaining <= 0 || p.isPrevBalancePay) return p;
      const deduct = Math.min(amountToDeduct, p.balanceRemaining);
      amountToDeduct -= deduct;
      return { ...p, balanceRemaining: Math.max(0, p.balanceRemaining - deduct) };
    });

    save({ ...db, pays: updatedPays, students: updatedStudents });
    setReceipt({ pay, s }); setShowRec(true);
    setPyAmt(''); setPyRef(''); setPyNote(''); setPyMn(''); setPyMns([]); setPyTuitionPartial(false); setPyTuitionPartialAmt(''); setPyExamFee(false); setPyExtras([]); setPyExtraPartial({}); setPyExtrasConcession(''); setPyConcession(''); setPyIncludeBalance(false); setEditingExtraIdx(null);
    toast('Recorded! ' + rc);
  };

  const printRec = () => {
    if (!receipt) return;
    const { pay, s } = receipt;
    const sch = db.settings.school || 'LORD KRISHNA PUBLIC SCHOOL';
    const addr = db.settings.addr?.trim() || '';
    const phone = db.settings.phone || '';
    // Full outstanding = this transaction's unpaid balance + all other source transactions' remaining balance
    const otherSourceBalance = db.pays
      .filter(p => p.sid === pay.sid && p.id !== pay.id && (p.balanceRemaining || 0) > 0 && !p.isPrevBalancePay)
      .reduce((sum, p) => sum + (p.balanceRemaining || 0), 0);
    const balanceRemaining = (pay.balanceRemaining || 0) + otherSourceBalance;
    const full = balanceRemaining === 0;
    const totalRemaining = (pay.tuitionRemaining || 0) + (pay.extrasRemaining || 0);

    const fmt = n => '\u20B9' + (n || 0).toLocaleString('en-IN');

    // Build charge rows
    const tuitionRow = pay.tuition > 0
      ? `<tr><td>Tuition${pay.mn ? ' \u2014 ' + pay.mn : ''}</td><td class="amt">${fmt(pay.tuition)}</td></tr>${pay.tuitionRemaining > 0 ? `<tr class="sub-row"><td>\u21B3 Remaining</td><td class="amt red">${fmt(pay.tuitionRemaining)}</td></tr>` : ''}` : '';
    const extrasRows = (pay.extras || []).map(e => {
      const rem = e.remaining > 0 ? `<tr class="sub-row"><td>\u21B3 Remaining</td><td class="amt amber">${fmt(e.remaining)}</td></tr>` : '';
      return `<tr><td>${e.label}</td><td class="amt">${fmt(parseFloat(e.amt) || 0)}</td></tr>${rem}`;
    }).join('');
    const concRow = pay.concession > 0 ? `<tr class="disc-row"><td>Discount</td><td class="amt green">\u2212 ${fmt(pay.concession)}</td></tr>` : '';
    const extrasConcRow = pay.extrasConcession > 0 ? `<tr class="disc-row"><td>Charges Discount</td><td class="amt amber">\u2212 ${fmt(pay.extrasConcession)}</td></tr>` : '';
    const examFeeRow = pay.examFee > 0 ? `<tr><td>Exam Fee</td><td class="amt purple">${fmt(pay.examFee)}</td></tr>` : '';
    const prevBalRow = pay.prevBalancePaid > 0 ? `<tr><td>Previous Balance</td><td class="amt">${fmt(pay.prevBalancePaid)}</td></tr>` : '';
    const noteRow = pay.note ? `<tr><td colspan="2" class="note">Note: ${pay.note}</td></tr>` : '';

    const css = `
      @page { size: 80mm auto; margin: 0; }
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body {
        font-family: 'Courier New', Courier, monospace;
        font-size: 11px;
        color: #000;
        background: #fff;
        width: 72mm;
        padding: 4mm 3mm 6mm;
      }
      /* ── Header ── */
      .hdr { text-align: center; margin-bottom: 6px; }
      .sch-name {
        font-size: 13px;
        font-weight: 700;
        letter-spacing: 0.5px;
        text-transform: uppercase;
        line-height: 1.3;
      }
      .sch-addr { font-size: 9px; color: #333; margin-top: 2px; line-height: 1.4; }
      .sch-phone { font-size: 9px; color: #333; }
      .divider { border: none; border-top: 1px dashed #000; margin: 5px 0; }
      .divider-solid { border: none; border-top: 1px solid #000; margin: 5px 0; }
      .divider-double { border: none; border-top: 3px double #000; margin: 5px 0; }
      .receipt-title {
        text-align: center;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 1px;
        text-transform: uppercase;
        margin: 4px 0;
      }
      /* ── Info rows ── */
      .info-table { width: 100%; border-collapse: collapse; margin-bottom: 4px; }
      .info-table td { font-size: 10px; padding: 1.5px 0; vertical-align: top; }
      .info-table td.lbl { width: 36%; color: #444; }
      .info-table td.sep { width: 4%; text-align: center; }
      .info-table td.val { font-weight: 600; }
      .info-table td.val.blue { /* no color on thermal */ }
      /* ── Charges table ── */
      .charges { width: 100%; border-collapse: collapse; margin: 4px 0; }
      .charges thead tr td {
        font-size: 9px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        padding: 2px 0;
        border-top: 1px dashed #000;
        border-bottom: 1px dashed #000;
      }
      .charges thead tr td.amt { text-align: right; }
      .charges tbody tr td { font-size: 10px; padding: 2px 0; }
      .charges tbody tr td.amt { text-align: right; font-weight: 600; }
      .charges tbody tr td.amt.green { /* thermal: keep black */ }
      .charges tbody tr td.amt.red { /* thermal: keep black */ }
      .charges tbody tr td.amt.amber { /* thermal: keep black */ }
      .charges tbody tr td.amt.purple { /* thermal: keep black */ }
      .sub-row td { font-size: 9px; padding-left: 8px; color: #444; }
      .disc-row td { font-size: 10px; }
      .note { font-size: 9px; color: #555; padding-top: 2px; }
      /* ── Total box ── */
      .total-box {
        border: 1px solid #000;
        padding: 4px 6px;
        margin: 5px 0 3px;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      .total-lbl { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; }
      .total-val { font-size: 14px; font-weight: 700; }
      /* ── Balance row ── */
      .balance-row {
        display: flex;
        justify-content: space-between;
        font-size: 10px;
        font-weight: 600;
        padding: 2px 0;
      }
      /* ── Remaining row ── */
      .remaining-row {
        display: flex;
        justify-content: space-between;
        font-size: 10px;
        font-weight: 600;
        padding: 2px 0;
        border-top: 1px dashed #000;
        margin-top: 3px;
        padding-top: 4px;
      }
      /* ── PAID stamp ── */
      .stamp-wrap { text-align: center; margin: 6px 0 4px; }
      .stamp {
        display: inline-block;
        border: 2px solid #000;
        font-size: 13px;
        font-weight: 700;
        letter-spacing: 3px;
        padding: 3px 16px;
        transform: rotate(-4deg);
        text-transform: uppercase;
      }
      /* ── Footer ── */
      .footer {
        text-align: center;
        font-size: 8px;
        color: #555;
        margin-top: 6px;
        line-height: 1.5;
      }
      .footer .thank { font-size: 10px; font-weight: 700; margin-bottom: 2px; }
      @media print {
        @page { size: 80mm auto; margin: 0; }
        body { padding: 3mm 3mm 8mm; }
      }
    `;

    const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Receipt ${pay.rc}</title><style>${css}</style></head>
<body>
  <div class="hdr">
    <div class="sch-name">${sch}</div>
    ${addr ? `<div class="sch-addr">${addr}</div>` : ''}
    ${phone ? `<div class="sch-phone">Ph: ${phone}</div>` : ''}
  </div>
  <hr class="divider-double">
  <div class="receipt-title">Fee Payment Receipt</div>
  <hr class="divider">

  <table class="info-table">
    <tr><td class="lbl">Receipt No.</td><td class="sep">:</td><td class="val">${pay.rc || '—'}</td></tr>
    <tr><td class="lbl">Date</td><td class="sep">:</td><td class="val">${pay.dt || '—'}</td></tr>
    <tr><td class="lbl">Student</td><td class="sep">:</td><td class="val">${s.fn} ${s.ln}</td></tr>
    <tr><td class="lbl">Class / Roll</td><td class="sep">:</td><td class="val">${s.cls || '—'} / ${s.roll || '—'}</td></tr>
    <tr><td class="lbl">Adm. No.</td><td class="sep">:</td><td class="val">${s.admno || '—'}</td></tr>
    <tr><td class="lbl">Father</td><td class="sep">:</td><td class="val">${s.father || s.fa || '—'}</td></tr>
    <tr><td class="lbl">Method</td><td class="sep">:</td><td class="val">${pay.md || 'Cash'}${pay.ref ? ' (' + pay.ref + ')' : ''}</td></tr>
  </table>

  <hr class="divider">
  <table class="charges">
    <thead><tr><td>Description</td><td class="amt">Amount</td></tr></thead>
    <tbody>
      ${tuitionRow}${extrasRows}${concRow}${extrasConcRow}${examFeeRow}${prevBalRow}${noteRow}
    </tbody>
  </table>
  <hr class="divider">

  <div class="total-box">
    <span class="total-lbl">Total Paid</span>
    <span class="total-val">${fmt(pay.amt)}</span>
  </div>

  <div class="balance-row">
    <span>Balance</span>
    <span>${fmt(balanceRemaining)}</span>
  </div>

  ${totalRemaining > 0 ? `<div class="remaining-row"><span>Total Remaining</span><span>${fmt(totalRemaining)}</span></div>` : ''}

  <hr class="divider">
  <div class="footer">
    <div class="thank">Thank You!</div>
    <div>This is a computer generated receipt.</div>
    <div>${sch}</div>
  </div>
  <script>window.onload = () => { window.focus(); window.print(); }<\/script>
</body></html>`;

    const w = window.open('', '_blank', 'width=320,height=600');
    if (w) { w.document.write(html); w.document.close(); }
  };

  return (
    <div className="finance-page p-6 space-y-8">
      {/* Page Header */}
      <div>
        <h2 className="text-4xl font-extrabold text-primary tracking-tight" style={{ fontFamily: 'Manrope,sans-serif' }}>Fees Management Portal</h2>
        <p className="text-on-surface-variant mt-1 font-medium tracking-wide text-sm">Real-time oversight of institutional cash flow and student liabilities.</p>
      </div>

      {/* Financial Summary Card — full width */}
      <div className="finance-summary-card bg-surface-container-lowest rounded-2xl p-8 relative overflow-hidden group border border-surface-container-high">
        <div className="absolute top-0 right-0 w-72 h-72 bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:scale-110 transition-transform duration-700" />
        <div className="relative z-10 space-y-6">
          <div className="finance-summary-top flex justify-between items-start">
            <div>
              <p className="text-[10px] font-bold text-on-surface-variant uppercase tracking-[0.1em] mb-1">Total Academic Collection</p>
              <h3 className="text-5xl font-extrabold text-primary tracking-tighter" style={{ fontFamily: 'Manrope,sans-serif' }}>₹{totalCollected.toLocaleString('en-IN')}</h3>
            </div>
            <div className="text-right">
              <p className="text-[10px] font-bold text-on-surface-variant uppercase tracking-[0.1em] mb-1">Total Outstanding</p>
              <h3 className="text-3xl font-extrabold text-error tracking-tight" style={{ fontFamily: 'Manrope,sans-serif' }}>₹{totalPending.toLocaleString('en-IN')}</h3>
            </div>
            <div className="text-right">
              <p className="text-[10px] font-bold text-on-surface-variant uppercase tracking-[0.1em] mb-1">Collection Rate</p>
              <h3 className="text-3xl font-extrabold tracking-tight" style={{ fontFamily: 'Manrope,sans-serif', color: '#059669' }}>{collectionRate}%</h3>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-[10px] font-bold uppercase text-on-surface-variant px-1">
              <span>Collected ({collectionRate}%)</span>
              <span>Remaining ({100 - collectionRate}%)</span>
            </div>
            <div className="h-3 w-full bg-surface-container-low rounded-full flex overflow-hidden">
              <div className="h-full bg-primary transition-all" style={{ width: collectionRate + '%' }} />
              <div className="h-full bg-error-container" style={{ width: (100 - collectionRate) + '%' }} />
            </div>
          </div>
        </div>
      </div>

      {/* Collect Fee Panel — full width below summary */}
      <div className="finance-collect-panel bg-primary-container rounded-2xl p-8 shadow-xl">
        <h3 className="text-xl font-bold mb-6 flex items-center gap-2 text-white" style={{ fontFamily: 'Manrope,sans-serif' }}>
          💳 New Finance Payment
        </h3>
        <div className="finance-collect-grid grid grid-cols-12 gap-6">
          {/* Left col: Class + Student selectors + Student card */}
          <div className="finance-collect-col col-span-4 space-y-4">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-on-primary-container mb-1.5">Step 1 — Select Class</label>
              <select value={selClass} onChange={e => { setSelClass(e.target.value); setSelStu(''); }}
                className="w-full bg-white/10 border-none rounded-xl px-4 py-3 text-sm text-white focus:ring-2 focus:ring-white/30 appearance-none">
                <option value="" className="text-primary">— Class —</option>
                {classes.map(c => <option key={c} value={c} className="text-primary">{c}</option>)}
              </select>
            </div>
            {selClass && (
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-on-primary-container mb-1.5">Step 2 — Select Student</label>
                <select value={selStu} onChange={e => { handleSelectStudent(e.target.value); }}
                  className="w-full bg-white/10 border-none rounded-xl px-4 py-3 text-sm text-white focus:ring-2 focus:ring-white/30 appearance-none">
                  <option value="" className="text-primary">— Student —</option>
                  {classStudents.map(s => <option key={s.id} value={s.id} className="text-primary">{s.fn} {s.ln} (Roll: {s.roll})</option>)}
                </select>
              </div>
            )}
            {/* Student Detail Card */}
            {piStu ? (
              <div className="bg-white/10 rounded-2xl p-5 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center text-white font-extrabold text-base flex-shrink-0 overflow-hidden">
                    {db.photos[piStu.id] || db.photos[piStu.sid]
                      ? <img src={db.photos[piStu.id] || db.photos[piStu.sid]} alt="" className="w-full h-full object-cover" />
                      : <>{piStu.fn?.[0]}{piStu.ln?.[0]}</>
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-base font-extrabold text-white leading-tight">{piStu.fn} {piStu.ln}</div>
                    <div className="text-[11px] text-on-primary-container mt-0.5">Class {piStu.cls} · Roll {piStu.roll}</div>
                    {piStu.admno && <div className="text-[11px] text-on-primary-container">Adm No: {piStu.admno}</div>}
                    {piStu.father && <div className="text-[11px] text-on-primary-container">Father: {piStu.father}</div>}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {(() => {
                    const isAnnual = piStu._payMode === 'annual';
                    const items = isAnnual ? [
                      ['Annual Fee', 'text-white', piStu._annualFee || 0],
                      ['Paid', 'text-emerald-300', piStu._paid || 0],
                      ['Balance', piStu._fullyPaid ? 'text-emerald-300' : 'text-red-300', Math.max(0, (piStu._annualFee || 0) - (piStu._paid || 0))]
                    ] : [
                      ['Monthly Fee', 'text-white', piStu._monthly || 0],
                      ['Total Paid', 'text-emerald-300', piStu._paid || 0],
                      ['Due Now', (piStu._overdueAmt || 0) > 0 ? 'text-red-300' : 'text-emerald-300', piStu._overdueAmt || 0]
                    ];
                    return items.map(([l, c, v]) => (
                      <div key={l} className="text-center bg-white/10 rounded-xl py-3">
                        <div className="text-[9px] text-on-primary-container uppercase tracking-wide mb-1">{l}</div>
                        <div className={'text-sm font-extrabold ' + c}>₹{(v || 0).toLocaleString('en-IN')}</div>
                      </div>
                    ));
                  })()}
                </div>
                <div>
                  <div className="flex justify-between text-[10px] text-on-primary-container mb-1">
                    <span>Fee collected</span><span className="font-bold text-white">{piPct}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-white/20 overflow-hidden">
                    <div className="h-full rounded-full bg-emerald-400 transition-all" style={{ width: piPct + '%' }} />
                  </div>
                </div>
                {piStu._overdue && !piStu._fullyPaid && piStu._payMode !== 'annual' && (
                  <div className="text-[11px] text-red-300 font-semibold bg-red-500/10 rounded-lg px-3 py-2">⚠ Overdue ~{piStu._overdueMonths} month{piStu._overdueMonths !== 1 ? 's' : ''} · ₹{piStu._overdueAmt.toLocaleString('en-IN')} pending</div>
                )}
                {piStu._payMode === 'annual' && !piStu._fullyPaid && (
                  <div className="text-[11px] text-red-300 font-semibold bg-red-500/10 rounded-lg px-3 py-2">⚠ Annual balance · ₹{Math.max(0, (piStu._annualFee || 0) - (piStu._paid || 0)).toLocaleString('en-IN')} remaining</div>
                )}
                {piStu._fullyPaid && (
                  <div className="text-[11px] text-emerald-300 font-semibold bg-emerald-500/10 rounded-lg px-3 py-2">✓ Fully Paid</div>
                )}
              </div>
            ) : (
              <div className="bg-white/5 rounded-2xl p-6 flex flex-col items-center justify-center text-center gap-2 border border-white/10" style={{ minHeight: '160px' }}>
                <div className="text-3xl opacity-30">🎓</div>
                <p className="text-[11px] text-on-primary-container">Select a class and student to begin</p>
              </div>
            )}
          </div>

          {/* Middle col: Month + Other Charges */}
          <div className="finance-collect-col col-span-4 space-y-4">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-on-primary-container mb-1.5">Step 3 — Select Months (Multiple Selection)</label>
              {piStu?._payMode === 'annual' ? (
                <button type="button"
                  onClick={() => { setPyMn('Annual'); if (piStu?._annualFee) setPyAmt(String(Math.max(0, (piStu._annualFee || 0) - (piStu._paid || 0)))); }}
                  className={'w-full rounded-xl px-4 py-3 text-sm font-bold transition-all border-0 cursor-pointer ' + (pyMn === 'Annual' ? 'bg-emerald-500/30 text-white ring-1 ring-emerald-400' : 'bg-white/10 text-white/70 hover:bg-white/20')}>
                  {pyMn === 'Annual' ? '✓ Annual Fee Selected' : 'Select Annual Fee'} {piStu._annualFee > 0 ? `— ₹${piStu._annualFee.toLocaleString('en-IN')}` : ''}
                </button>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-1.5 mb-2">
                    {availableMonths.map(m => {
                      const sel = pyMns.includes(m);
                      return (
                        <button key={m} type="button"
                          onClick={() => { setPyMns(sel ? pyMns.filter(x => x !== m) : [...pyMns, m]); setPyTuitionPartial(false); setPyTuitionPartialAmt(''); }}
                          className={'rounded-lg px-2 py-1.5 text-[11px] font-bold transition-all border-0 cursor-pointer ' + (sel ? 'bg-emerald-500/30 text-white ring-1 ring-emerald-400' : 'bg-white/10 text-white/60 hover:bg-white/20')}>
                          {sel ? '✓ ' : ''}{m.slice(0, 3)}
                        </button>
                      );
                    })}
                    {availableMonths.length === 0 && <div className="col-span-3 text-[11px] text-emerald-300 font-semibold text-center py-2">✓ All months paid</div>}
                  </div>
                  {pyMns.length > 0 && (
                    <div className="text-[10px] text-on-primary-container mb-1">
                      {pyMns.length} month{pyMns.length > 1 ? 's' : ''} · ₹{monthlyBase.toLocaleString('en-IN')} total
                    </div>
                  )}
                  {paidMonths.size > 0 && (
                    <div className="text-[10px] text-on-primary-container mt-1.5 leading-relaxed">Already paid: {[...paidMonths].join(', ')}</div>
                  )}
                </>
              )}
            </div>
            {/* Exam Fee toggle — only for monthly mode, only if not already paid */}
            {!isAnnualMode && stuExamFee > 0 && !examFeeAlreadyPaid && (
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-on-primary-container mb-1.5">Examination Fee</label>
                <button type="button"
                  onClick={() => setPyExamFee(v => !v)}
                  className={'w-full flex items-center justify-between px-4 py-2.5 rounded-xl text-sm font-bold transition-all border-0 cursor-pointer ' + (pyExamFee ? 'bg-purple-500/30 text-white ring-1 ring-purple-400' : 'bg-white/10 text-white/70 hover:bg-white/20')}>
                  <span className="flex items-center gap-2.5">
                    <span className={'w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 text-[11px] font-bold ' + (pyExamFee ? 'bg-purple-400 text-white' : 'bg-white/20 text-white/40')}>{pyExamFee ? '✓' : ''}</span>
                    Examination Fee <span className="text-[10px] font-normal opacity-70">(one-time)</span>
                  </span>
                  <span className={pyExamFee ? 'text-purple-300 font-bold' : 'text-white/50'}>₹{examFeeRemaining.toLocaleString('en-IN')}</span>
                </button>
              </div>
            )}
            {!isAnnualMode && stuExamFee > 0 && examFeeAlreadyPaid && (
              <div className="text-[11px] text-emerald-300 font-semibold bg-emerald-500/10 rounded-lg px-3 py-2">✓ Examination fee already paid</div>
            )}
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-on-primary-container mb-1.5">Other Charges</label>
              {(piStu?.fextras || []).length > 0 ? (
                <div className="space-y-2 mb-3">
                  {(piStu.fextras || []).map((ex, i) => {
                    const selected = pyExtras.includes(i);
                    const fullAmt = parseFloat(ex.amt) || 0;
                    const alreadyPaid = (piStu.paidExtras || {})[ex.label] || 0;
                    const remainingAmt = Math.max(0, fullAmt - alreadyPaid);
                    // Compute actual paid amount from payment history (more reliable than paidExtras)
                    const paidFromHistory = db.pays
                      .filter(p => p.sid === selStu)
                      .reduce((sum, p) => sum + ((p.extras || []).filter(e => e.label === ex.label).reduce((s, e) => s + (parseFloat(e.amt) || 0), 0)), 0);
                    // Only mark as history if actual money was paid for this charge
                    const hasHistory = paidFromHistory > 0;
                    const isEditing = editingExtraIdx === i;
                    return (
                      <div key={i} className="space-y-1.5">
                        {hasHistory ? (
                          /* Read-only history item — charge was already transacted */
                          <div className="flex items-center justify-between px-4 py-2.5 rounded-xl bg-emerald-500/10 ring-1 ring-emerald-500/30 text-sm">
                            <span className="flex items-center gap-2.5 text-emerald-300 font-semibold">
                              <span className="w-5 h-5 rounded-md bg-emerald-400 text-white flex items-center justify-center flex-shrink-0 text-[11px] font-bold">✓</span>
                              {ex.label}
                            </span>
                            <div className="flex items-center gap-2">
                              <button type="button" title="View Receipt" onClick={() => {
                                const txn = [...db.pays].filter(p => p.sid === selStu && (p.extras || []).some(e => e.label === ex.label && (parseFloat(e.amt) || 0) > 0)).sort((a, b) => new Date(b.dt) - new Date(a.dt))[0];
                                if (txn) { const s = db.students.find(x => x.id === selStu); if (s) { setReceipt({ pay: txn, s }); setShowRec(true); } }
                              }} className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-white/60 hover:text-white text-[11px] font-semibold transition-all border-0 cursor-pointer flex-shrink-0">
                                🧾 Receipt
                              </button>
                              <button type="button" title="Delete" onClick={() => {
                                if (!window.confirm('Remove "' + ex.label + '" from this student\'s charges?')) return;
                                const updatedStudents = db.students.map(x => {
                                  if (x.id !== selStu) return x;
                                  const fextras = (x.fextras || []).filter((_, j) => j !== i);
                                  const extrasSum = fextras.reduce((s, e) => s + (parseFloat(e.amt) || 0), 0);
                                  return { ...x, fextras, fee: (x.mf || 0) * 12 + extrasSum };
                                });
                                save({ ...db, students: updatedStudents });
                                setPyExtras(pyExtras.filter(x => x !== i).map(x => x > i ? x - 1 : x));
                                setPyExtraPartial(p => { const n = {}; Object.entries(p).forEach(([k, v]) => { const ki = parseInt(k); if (ki < i) n[k] = v; else if (ki > i) n[ki - 1] = v; }); return n; });
                                if (editingExtraIdx === i) setEditingExtraIdx(null);
                              }} className="w-6 h-6 rounded-lg bg-white/10 hover:bg-red-500/30 text-white/40 hover:text-red-300 text-xs flex items-center justify-center transition-all border-0 cursor-pointer flex-shrink-0">✕</button>
                            </div>
                          </div>
                        ) : isEditing ? (
                          <div className="flex items-center gap-1.5">
                            <input value={editingExtraLabel} onChange={e => setEditingExtraLabel(e.target.value)}
                              className="flex-1 bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-sm text-white placeholder:text-white/40 focus:ring-2 focus:ring-white/30"
                              placeholder="Charge name" autoFocus />
                            <input type="number" value={editingExtraAmt} onChange={e => setEditingExtraAmt(e.target.value)}
                              className="w-24 bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-sm text-white placeholder:text-white/40 focus:ring-2 focus:ring-white/30"
                              placeholder="₹" />
                            <button type="button" title="Save" onClick={() => {
                              if (!editingExtraLabel.trim() || !parseFloat(editingExtraAmt)) { toast('Enter valid name and amount', 'err'); return; }
                              const updatedStudents = db.students.map(x => {
                                if (x.id !== selStu) return x;
                                const fextras = (x.fextras || []).map((e, j) => j === i ? { label: editingExtraLabel.trim(), amt: editingExtraAmt } : e);
                                const extrasSum = fextras.reduce((s, e) => s + (parseFloat(e.amt) || 0), 0);
                                return { ...x, fextras, fee: (x.mf || 0) * 12 + extrasSum };
                              });
                              save({ ...db, students: updatedStudents });
                              setEditingExtraIdx(null);
                              toast('Charge updated');
                            }} className="w-8 h-8 rounded-lg bg-emerald-500/30 hover:bg-emerald-500/50 text-emerald-300 text-xs flex items-center justify-center transition-all border-0 cursor-pointer flex-shrink-0">✓</button>
                            <button type="button" title="Cancel" onClick={() => setEditingExtraIdx(null)}
                              className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 text-white/60 text-xs flex items-center justify-center transition-all border-0 cursor-pointer flex-shrink-0">✕</button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5">
                            <button type="button"
                              onClick={() => {
                                if (selected) { setPyExtras(pyExtras.filter(x => x !== i)); setPyExtraPartial(p => { const n = { ...p }; delete n[i]; return n; }); }
                                else setPyExtras([...pyExtras, i]);
                              }}
                              className={'flex-1 flex items-center justify-between px-4 py-2.5 rounded-xl text-sm font-semibold transition-all border-0 cursor-pointer ' + (selected ? 'bg-emerald-500/30 text-white ring-1 ring-emerald-400' : 'bg-white/10 text-white/70 hover:bg-white/20')}>
                              <span className="flex items-center gap-2.5">
                                <span className={'w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 text-[11px] font-bold ' + (selected ? 'bg-emerald-400 text-white' : 'bg-white/20 text-white/40')}>{selected ? '✓' : ''}</span>
                                {ex.label}
                              </span>
                              <div className="text-right leading-tight">
                                <div className={selected ? 'text-emerald-300 font-bold' : 'text-white/50'}>₹{remainingAmt.toLocaleString('en-IN')}</div>
                              </div>
                            </button>
                            <button type="button" title="Edit" onClick={() => {
                              setEditingExtraIdx(i);
                              setEditingExtraLabel(ex.label);
                              setEditingExtraAmt(ex.amt);
                            }} className="w-7 h-7 rounded-lg bg-white/10 hover:bg-white/20 text-white/60 hover:text-white text-xs flex items-center justify-center transition-all border-0 cursor-pointer flex-shrink-0">✎</button>
                            <button type="button" title="Delete" onClick={() => {
                              if (!window.confirm('Remove "' + ex.label + '" from this student\'s charges?')) return;
                              const updatedStudents = db.students.map(x => {
                                if (x.id !== selStu) return x;
                                const fextras = (x.fextras || []).filter((_, j) => j !== i);
                                const extrasSum = fextras.reduce((s, e) => s + (parseFloat(e.amt) || 0), 0);
                                return { ...x, fextras, fee: (x.mf || 0) * 12 + extrasSum };
                              });
                              save({ ...db, students: updatedStudents });
                              setPyExtras(pyExtras.filter(x => x !== i).map(x => x > i ? x - 1 : x));
                              setPyExtraPartial(p => { const n = {}; Object.entries(p).forEach(([k, v]) => { const ki = parseInt(k); if (ki < i) n[k] = v; else if (ki > i) n[ki - 1] = v; }); return n; });
                              if (editingExtraIdx === i) setEditingExtraIdx(null);
                            }} className="w-7 h-7 rounded-lg bg-white/10 hover:bg-red-500/30 text-white/60 hover:text-red-300 text-xs flex items-center justify-center transition-all border-0 cursor-pointer flex-shrink-0">✕</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : piStu ? (
                <p className="text-[11px] text-on-primary-container mb-2">No charges defined. Add one below.</p>
              ) : null}
              <div className="finance-charge-create flex gap-2">
                <input value={pyNewExLabel} onChange={e => setPyNewExLabel(e.target.value)} placeholder="Charge name"
                  className="flex-1 bg-white/10 border-none rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-white/40 focus:ring-2 focus:ring-white/30" />
                <input type="number" value={pyNewExAmt} onChange={e => setPyNewExAmt(e.target.value)} placeholder="₹"
                  className="w-20 bg-white/10 border-none rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-white/40 focus:ring-2 focus:ring-white/30" />
                <button type="button" onClick={() => {
                  if (!pyNewExLabel.trim() || !parseFloat(pyNewExAmt)) { toast('Enter name and amount', 'err'); return; }
                  const newEx = { label: pyNewExLabel.trim(), amt: pyNewExAmt };
                  const updatedStudents = db.students.map(x => {
                    if (x.id !== selStu) return x;
                    const fextras = [...(x.fextras || []), newEx];
                    const extrasSum = fextras.reduce((s, e) => s + (parseFloat(e.amt) || 0), 0);
                    // Clear any stale paidExtras for this label so it shows the correct amount
                    const paidExtras = { ...(x.paidExtras || {}) };
                    delete paidExtras[newEx.label];
                    return { ...x, fextras, paidExtras, fee: (x.mf || 0) * 12 + extrasSum };
                  });
                  save({ ...db, students: updatedStudents });
                  const newIdx = (piStu?.fextras || []).length;
                  setPyExtras([...pyExtras, newIdx]);
                  setPyExtraPartial(p => ({ ...p }));
                  setPyNewExLabel(''); setPyNewExAmt('');
                  toast('Charge added');
                }} className="w-10 h-10 rounded-xl bg-white/20 hover:bg-white/30 text-white font-bold text-lg flex items-center justify-center transition-all flex-shrink-0 border-0 cursor-pointer">+</button>
              </div>
            </div>
          </div>

          {/* Right col: Payment details + total + submit */}
          <div className="finance-collect-col col-span-4 space-y-4">
            <div className="finance-payment-grid grid grid-cols-1 gap-3">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-on-primary-container mb-1.5">Payment Mode</label>
                <select value={pyMd} onChange={e => setPyMd(e.target.value)}
                  className="w-full bg-white/10 border-none rounded-xl px-4 py-3 text-sm text-white focus:ring-2 focus:ring-white/30 appearance-none">
                  {['Cash', 'UPI', 'Online', 'Cheque', 'DD'].map(m => <option key={m} className="text-primary">{m}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-on-primary-container mb-1.5">Discount (₹)</label>
              <input type="number" value={pyConcession} onChange={e => setPyConcession(e.target.value)}
                placeholder="0" min="0" max={pyGrossTotal}
                className="w-full bg-white/10 border-none rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/40 focus:ring-2 focus:ring-white/30" />
              {parseFloat(pyConcession) > pyGrossTotal && (
                <div className="text-[10px] text-red-300 mt-1">Discount cannot exceed total amount (₹{pyGrossTotal.toLocaleString('en-IN')})</div>
              )}
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-on-primary-container mb-1.5">Paying Amount (₹)</label>
              <input type="number" value={pyAmt} onChange={e => setPyAmt(e.target.value)}
                placeholder="Amount student is paying now"
                className="w-full bg-white/10 border-none rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/40 focus:ring-2 focus:ring-white/30" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-on-primary-container mb-1.5">Payment Date</label>
              <input type="date" value={pyDt} onChange={e => setPyDt(e.target.value)}
                className="w-full bg-white/10 border-none rounded-xl px-4 py-3 text-sm text-white focus:ring-2 focus:ring-white/30" />
            </div>
            {/* Previous Balance */}
            {piStu && (
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-on-primary-container mb-1.5">Previous Balance</label>
                {totalPrevBalance === 0 ? (
                  <div className="w-full flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-[11px] text-white/40">
                    <span>No balance</span>
                  </div>
                ) : (
                  <button type="button"
                    onClick={() => setPyIncludeBalance(v => !v)}
                    className={'w-full flex items-center justify-between px-4 py-2.5 rounded-xl text-sm font-semibold transition-all border-0 cursor-pointer ' + (pyIncludeBalance ? 'bg-orange-500/30 text-white ring-1 ring-orange-400' : 'bg-white/10 text-white/70 hover:bg-white/20')}>
                    <span className="flex items-center gap-2.5">
                      <span className={'w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 text-[11px] font-bold ' + (pyIncludeBalance ? 'bg-orange-400 text-white' : 'bg-white/20 text-white/40')}>{pyIncludeBalance ? '✓' : ''}</span>
                      Previous Balance
                    </span>
                    <div className={pyIncludeBalance ? 'text-orange-300 font-bold' : 'text-white/50'}>₹{totalPrevBalance.toLocaleString('en-IN')}</div>
                  </button>
                )}
              </div>
            )}
            {/* Balance info box */}
            {piStu && (
              <div className="bg-white/5 rounded-2xl px-5 py-4 space-y-2 border border-white/10">
                {piStu._payMode === 'annual' ? (
                  <>
                    <div className="flex justify-between text-sm text-on-primary-container">
                      <span>Annual Fee</span>
                      <span className="font-bold text-white">₹{(piStu._annualFee || 0).toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between text-sm text-on-primary-container">
                      <span>Already Paid</span>
                      <span className="font-bold text-emerald-300">₹{(piStu._paid || 0).toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between text-sm text-on-primary-container" style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 8 }}>
                      <span>Current Balance</span>
                      <span className="font-bold text-red-300">₹{Math.max(0, (piStu._annualFee || 0) - (piStu._paid || 0)).toLocaleString('en-IN')}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between text-sm text-on-primary-container">
                      <span>Monthly Fee</span>
                      <span className="font-bold text-white">₹{(piStu._monthly || 0).toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between text-sm text-on-primary-container">
                      <span>Months elapsed</span>
                      <span className="font-bold text-white">{monthsElapsed}</span>
                    </div>
                    <div className="flex justify-between text-sm text-on-primary-container" style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 8 }}>
                      <span>Currently Due</span>
                      <span className={`font-bold ${(piStu._overdueAmt || 0) > 0 ? 'text-red-300' : 'text-emerald-300'}`}>
                        {(piStu._overdueAmt || 0) > 0 ? `₹${piStu._overdueAmt.toLocaleString('en-IN')}` : '✓ Paid'}
                      </span>
                    </div>
                  </>
                )}
              </div>
            )}
            <div className="bg-white/10 rounded-2xl px-5 py-4 space-y-2">
              {hasTuition && (
                <div className="flex justify-between text-sm text-on-primary-container">
                  <span>Tuition {!isAnnualMode && pyMns.length > 0 ? `(${pyMns.length} mo)` : ''}</span>
                  <span className="font-bold text-white">₹{effectiveTuition.toLocaleString('en-IN')}</span>
                </div>
              )}
              {pyExamFee && examFeeAmt > 0 && (
                <div className="flex justify-between text-sm text-on-primary-container">
                  <span>Examination Fee</span>
                  <span className="font-bold text-purple-300">₹{examFeeAmt.toLocaleString('en-IN')}</span>
                </div>
              )}
              {(piStu?.fextras || []).filter((_, i) => pyExtras.includes(i)).map((ex, i) => {
                const remainingAmt = Math.max(0, (parseFloat(ex.amt) || 0) - ((piStu.paidExtras || {})[ex.label] || 0));
                return (
                  <div key={i} className="flex justify-between text-sm text-on-primary-container">
                    <span>{ex.label}</span>
                    <span className="font-bold text-emerald-300">₹{remainingAmt.toLocaleString('en-IN')}</span>
                  </div>
                );
              })}
              {prevBalance > 0 && (
                <div className="flex justify-between text-sm text-on-primary-container">
                  <span>Previous Balance</span>
                  <span className="font-bold text-orange-300">₹{prevBalance.toLocaleString('en-IN')}</span>
                </div>
              )}
              {concessionAmt > 0 && (
                <div className="flex justify-between text-sm text-on-primary-container">
                  <span>Discount</span>
                  <span className="font-bold text-yellow-300">− ₹{concessionAmt.toLocaleString('en-IN')}</span>
                </div>
              )}
              <div className="flex justify-between text-sm font-bold text-white" style={{ borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: 8 }}>
                <span>Total to Pay</span>
                <span>₹{pyEffectiveNet.toLocaleString('en-IN')}</span>
              </div>
              {parseFloat(pyAmt) >= 0 && pyEffectiveNet > 0 && (
                <>
                  <div className="flex justify-between text-sm text-on-primary-container">
                    <span>Paying Now</span>
                    <span className="font-bold text-blue-300">₹{(parseFloat(pyAmt) || 0).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between text-base font-bold text-amber-300" style={{ borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: 8 }}>
                    <span>Balance Remaining</span>
                    <span>₹{(Math.max(0, pyEffectiveNet - (parseFloat(pyAmt) || 0)) + Math.max(0, totalPrevBalance - prevBalance)).toLocaleString('en-IN')}</span>
                  </div>
                </>
              )}
            </div>
            {parseFloat(pyAmt) > 0 && Math.max(0, pyEffectiveNet - parseFloat(pyAmt)) === 0 && (
              <div className="text-[12px] text-emerald-300 font-semibold bg-emerald-500/10 rounded-xl px-4 py-2.5">
                ✓ No pending dues after this payment
              </div>
            )}
            {parseFloat(pyConcession) >= pyGrossTotal && pyGrossTotal > 0 && (
              <div className="text-[12px] text-emerald-300 font-semibold bg-emerald-500/10 rounded-xl px-4 py-2.5">
                ✓ Full discount applied — ₹0 transaction allowed
              </div>
            )}
            <button onClick={recPay} disabled={!selStu || (parseFloat(pyAmt) <= 0 && pyEffectiveNet > 0)}
              className="w-full bg-secondary-container hover:bg-white text-primary font-bold py-4 rounded-xl transition-all flex items-center justify-center gap-2 text-base disabled:opacity-40 disabled:cursor-not-allowed">
              Record Transaction →
            </button>
          </div>
        </div>
      </div>

      {/* Tabs: Overdue / History / Ledger / Fee Structure */}
      <div>
        <div className="finance-tabs flex gap-1 mb-4 bg-surface-container-low rounded-xl p-1 w-fit">
          {['Overdue Accounts', 'Payment History', 'Student Ledger', 'Fee Structure'].map((t, i) => (
            <button key={t} onClick={() => setTab(i)}
              className={'px-4 py-2 rounded-lg text-xs font-bold transition-all ' + (tab === i ? 'bg-white text-primary shadow-sm' : 'text-on-surface-variant hover:text-primary')}>
              {t}
            </button>
          ))}
        </div>

        {/* Overdue Accounts Table */}
        {tab === 0 && (
          <div className="bg-surface-container-low rounded-2xl p-1">
            <div className="bg-surface-container-lowest rounded-2xl overflow-hidden">
              <div className="px-6 py-5 flex justify-between items-center border-b border-surface-container-low flex-wrap gap-3">
                <div>
                  <h3 className="text-xl font-bold text-primary" style={{ fontFamily: 'Manrope,sans-serif' }}>Overdue Accounts</h3>
                  <p className="text-xs text-on-surface-variant mt-0.5">Students with fee overdue by at least 1 month</p>
                </div>
                <div className="flex items-center gap-3">
                  <select value={overdueCls} onChange={e => setOverdueCls(e.target.value)}
                    className="bg-surface-container-low border border-outline-variant rounded-xl px-3 py-2 text-sm text-on-surface focus:outline-none">
                    <option value="">All Classes</option>
                    {[...new Set(db.students.map(s => s.cls))].sort().map(c => <option key={c}>{c}</option>)}
                  </select>
                  <span className="text-xs font-bold text-on-surface-variant">
                    {overdueRows.filter(s => !overdueCls || s.cls === overdueCls).length} overdue
                  </span>
                </div>
              </div>
              <div className="overflow-x-auto finance-overdue-table">
                <table className="w-full text-left">
                  <thead className="bg-surface-container-low/30">
                    <tr>
                      {['Student Name', 'Class', 'Status', 'Months Overdue', 'Amount Due', 'Actions'].map(h => (
                        <th key={h} className="px-6 py-4 text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-surface-container-low">
                    {overdueRows.filter(s => !overdueCls || s.cls === overdueCls).map(s => (
                      <tr key={s.id} className="group hover:bg-surface-container-low transition-colors">
                        <td className="px-6 py-5">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-primary-container flex items-center justify-center text-on-primary-container font-bold text-sm flex-shrink-0">
                              {s.fn?.[0]}{s.ln?.[0]}
                            </div>
                            <div>
                              <p className="text-sm font-bold text-primary">{s.fn} {s.ln}</p>
                              <p className="text-[10px] text-on-surface-variant font-medium">{s.admno || s.roll || '—'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-5 text-sm font-medium text-on-surface">{s.cls}</td>
                        <td className="px-6 py-5">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold bg-error-container text-error uppercase tracking-wide">
                            Overdue
                          </span>
                        </td>
                        <td className="px-6 py-5 text-sm font-bold text-error">{s._overdueMonths} mo.</td>
                        <td className="px-6 py-5 text-sm font-extrabold text-primary">₹{s._overdueAmt.toLocaleString('en-IN')}</td>
                        <td className="px-6 py-5">
                          <button onClick={() => { setSelClass(s.cls); setSelStu(s.id); }}
                            className="bg-primary hover:bg-primary-container text-white px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5">
                            💳 Collect Fee
                          </button>
                        </td>
                      </tr>
                    ))}
                    {!overdueRows.filter(s => !overdueCls || s.cls === overdueCls).length && (
                      <tr><td colSpan={6} className="px-6 py-12 text-center text-on-surface-variant text-sm">
                        <div className="text-3xl mb-2">✅</div>No overdue accounts — all students are up to date!
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="finance-overdue-mobile-list">
                {overdueRows.filter(s => !overdueCls || s.cls === overdueCls).map(s => (
                  <div key={s.id} className="finance-mobile-card">
                    <div className="finance-mobile-card-head">
                      <div>
                        <div className="finance-mobile-title">{s.fn} {s.ln}</div>
                        <div className="finance-mobile-sub">{s.cls} · {s.admno || s.roll || '—'}</div>
                      </div>
                      <span className="finance-mobile-badge finance-mobile-badge-error">Overdue</span>
                    </div>
                    <div className="finance-mobile-metrics">
                      <div>
                        <span>Months overdue</span>
                        <strong>{s._overdueMonths} mo.</strong>
                      </div>
                      <div>
                        <span>Amount due</span>
                        <strong>₹{s._overdueAmt.toLocaleString('en-IN')}</strong>
                      </div>
                    </div>
                    <button onClick={() => { setSelClass(s.cls); setSelStu(s.id); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      className="finance-mobile-action">
                      Collect Fee
                    </button>
                  </div>
                ))}
                {!overdueRows.length && (
                  <div className="finance-mobile-empty">
                    <div className="finance-mobile-empty-icon">✅</div>
                    <div>No overdue accounts — all students are up to date.</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Payment History */}
        {tab === 1 && (() => {
          const filteredPays = histStu
            ? [...db.pays].filter(p => p.sid === histStu).reverse()
            : [...db.pays].reverse();
          const histStudent = histStu ? db.students.find(x => x.id === histStu) : null;
          const histTotal = filteredPays.reduce((s, p) => s + p.amt, 0);
          return (
            <div>
              {/* Student filter bar */}
              <div className="finance-history-filter flex items-center gap-3 mb-4 flex-wrap">
                <div className="flex items-center gap-2 bg-surface-container-low rounded-xl p-1">
                  <button onClick={() => setHistStu('')}
                    className={'px-3 py-1.5 rounded-lg text-xs font-bold transition-all ' + (histStu === '' ? 'bg-white text-primary shadow-sm' : 'text-on-surface-variant hover:text-primary')}>
                    All Students
                  </button>
                </div>
                <Select value={histStu} onChange={setHistStu} style={{ width: 240 }}>
                  <option value="">— Filter by Student —</option>
                  {db.students.map(s => <option key={s.id} value={s.id}>{s.fn} {s.ln} ({s.cls})</option>)}
                </Select>
                {histStudent && (
                  <div className="flex items-center gap-2 bg-primary/5 border border-primary/20 rounded-xl px-3 py-1.5">
                    <div className="w-6 h-6 rounded-md bg-primary-container flex items-center justify-center text-on-primary-container font-bold text-[10px]">
                      {histStudent.fn?.[0]}{histStudent.ln?.[0]}
                    </div>
                    <span className="text-xs font-bold text-primary">{histStudent.fn} {histStudent.ln}</span>
                    <span className="text-[10px] text-on-surface-variant">· {filteredPays.length} txn{filteredPays.length !== 1 ? 's' : ''} · ₹{histTotal.toLocaleString('en-IN')}</span>
                    <button onClick={() => setHistStu('')} className="text-outline hover:text-error text-xs ml-1">✕</button>
                  </div>
                )}
                {!histStudent && db.pays.length > 0 && (
                  <span className="text-xs text-on-surface-variant">{filteredPays.length} transaction{filteredPays.length !== 1 ? 's' : ''} · ₹{histTotal.toLocaleString('en-IN')} total</span>
                )}
              </div>
              <TblWrap className="finance-history-table">
                <table>
                  <thead><tr>
                    <th>Date</th><th>Receipt</th><th>Student</th><th>Class</th><th>Month</th><th>Type</th><th>Amount</th><th>Method</th><th>Balance After</th><th></th>
                  </tr></thead>
                  <tbody>
                    {filteredPays.map(p => (
                      <tr key={p.id}>
                        <td className="whitespace-nowrap">{p.dt}</td>
                        <td><Badge color="blue">{p.rc}</Badge></td>
                        <td className="font-medium">{p.nm}</td>
                        <td><Badge color="blue">{p.cls}</Badge></td>
                        <td className="text-on-surface-variant">{p.mn || '—'}</td>
                        <td className="text-on-surface-variant">{p.ty || '—'}</td>
                        <td className="font-bold text-emerald-600">₹{(p.totalAmt || p.amt || 0).toLocaleString('en-IN')}</td>
                        <td>{p.md}</td>
                        <td className={'font-semibold ' + (p.ba === 0 ? 'text-emerald-600' : 'text-error')}>₹{(p.ba || 0).toLocaleString('en-IN')}</td>
                        <td><Btn size="sm" onClick={() => { const s = db.students.find(x => x.id === p.sid); if (s) { setReceipt({ pay: p, s }); setShowRec(true); } }}>Receipt</Btn></td>
                      </tr>
                    ))}
                    {!filteredPays.length && <tr><td colSpan={10}><NoData text={histStu ? 'No payments for this student' : 'No payments recorded'} /></td></tr>}
                  </tbody>
                </table>
              </TblWrap>
              <div className="finance-history-mobile-list">
                {filteredPays.map(p => (
                  <div key={p.id} className="finance-mobile-card">
                    <div className="finance-mobile-card-head">
                      <div>
                        <div className="finance-mobile-title">{p.nm}</div>
                        <div className="finance-mobile-sub">{p.cls} · {p.dt}</div>
                      </div>
                      <span className="finance-mobile-badge finance-mobile-badge-success">₹{(p.totalAmt || p.amt || 0).toLocaleString('en-IN')}</span>
                    </div>
                    <div className="finance-mobile-meta-list">
                      <div><span>Receipt</span><strong>{p.rc}</strong></div>
                      <div><span>Month</span><strong>{p.mn || '—'}</strong></div>
                      <div><span>Method</span><strong>{p.md}</strong></div>
                      <div><span>Balance after</span><strong>₹{(p.ba || 0).toLocaleString('en-IN')}</strong></div>
                    </div>
                    <button className="finance-mobile-action finance-mobile-action-secondary" onClick={() => { const s = db.students.find(x => x.id === p.sid); if (s) { setReceipt({ pay: p, s }); setShowRec(true); } }}>
                      View Receipt
                    </button>
                  </div>
                ))}
                {!filteredPays.length && (
                  <div className="finance-mobile-empty">
                    <div className="finance-mobile-empty-icon">🧾</div>
                    <div>{histStu ? 'No payments for this student' : 'No payments recorded'}</div>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Student Ledger */}
        {tab === 2 && (
          <div>
            {/* Class + Student filter */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Filter by Class</div>
                <select value={ledCls || ''} onChange={e => { setLedCls(e.target.value); setLedStu(''); }}
                  style={{ padding: '9px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: 13, color: '#1e293b', outline: 'none', minWidth: 140, cursor: 'pointer' }}>
                  <option value="">All Classes</option>
                  {[...new Set(db.students.map(s => s.cls))].sort().map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Select Student</div>
                <select value={ledStu} onChange={e => setLedStu(e.target.value)}
                  style={{ width: '100%', padding: '9px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: 13, color: '#1e293b', outline: 'none', cursor: 'pointer' }}>
                  <option value="">— Select Student —</option>
                  {db.students.filter(s => !ledCls || s.cls === ledCls).map(s => <option key={s.id} value={s.id}>{s.fn} {s.ln} ({s.cls})</option>)}
                </select>
              </div>
            </div>

            {ledStu ? (() => {
              const s = db.students.find(x => x.id === ledStu); if (!s) return null;
              const cf = classFeeMap[s.cls] || {};
              const monthly = parseFloat(cf.monthly) || 0;
              const examFee = parseFloat(cf.exam) || 0;
              const baseAnnualFee = monthly > 0 ? monthly * 12 + examFee : 0;
              const pays = db.pays.filter(p => p.sid === ledStu).sort((a, b) => new Date(b.dt) - new Date(a.dt));
              const totalConcession = pays.reduce((sum, p) => sum + (p.concession || 0), 0);
              const annualFee = Math.max(0, baseAnnualFee - totalConcession);
              const tp = pays.reduce((sum, p) => sum + p.amt, 0);
              const bal = Math.max(0, annualFee - tp);
              const full = annualFee > 0 && bal <= 0;
              const pct = annualFee > 0 ? Math.min(100, Math.round(tp / annualFee * 100)) : 0;
              let run = annualFee;
              return (
                <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e8edf5', overflow: 'hidden' }}>
                  {/* Student Header */}
                  <div style={{ padding: '20px 24px', background: 'linear-gradient(135deg,#1960a3,#2563eb)', color: '#fff' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <div style={{ width: 52, height: 52, borderRadius: 14, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 18, flexShrink: 0 }}>
                          {(s.fn || '')[0]}{(s.ln || '')[0]}
                        </div>
                        <div>
                          <div style={{ fontSize: 18, fontWeight: 800, fontFamily: 'Manrope,sans-serif' }}>{s.fn} {s.ln}</div>
                          <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>Class {s.cls} · Roll {s.roll || '—'} · Adm: {s.admno || '—'}</div>
                          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 1 }}>Father: {s.father || '—'} · {s.fphone || s.ph || '—'}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        {[
                          { l: 'Annual Fee', v: `₹${annualFee.toLocaleString('en-IN')}`, c: 'rgba(255,255,255,0.2)', tc: '#fff' },
                          { l: 'Paid', v: `₹${tp.toLocaleString('en-IN')}`, c: 'rgba(16,185,129,0.3)', tc: '#6ee7b7' },
                          { l: 'Balance', v: `₹${bal.toLocaleString('en-IN')}`, c: full ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)', tc: full ? '#6ee7b7' : '#fca5a5' },
                        ].map(({ l, v, c, tc }) => (
                          <div key={l} style={{ textAlign: 'center', background: c, borderRadius: 10, padding: '8px 16px', minWidth: 90 }}>
                            <div style={{ fontSize: 10, opacity: 0.8, marginBottom: 2 }}>{l}</div>
                            <div style={{ fontSize: 15, fontWeight: 800, color: tc }}>{v}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* Progress bar */}
                    <div style={{ marginTop: 16 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, opacity: 0.8, marginBottom: 6 }}>
                        <span>Fee collected</span><span style={{ fontWeight: 700 }}>{pct}%</span>
                      </div>
                      <div style={{ height: 6, borderRadius: 999, background: 'rgba(255,255,255,0.2)', overflow: 'hidden' }}>
                        <div style={{ height: '100%', borderRadius: 999, background: full ? '#6ee7b7' : '#fbbf24', width: pct + '%', transition: 'width 0.5s' }} />
                      </div>
                    </div>
                  </div>

                  {/* Fee Breakdown */}
                  {annualFee > 0 && (
                    <div style={{ padding: '16px 24px', borderBottom: '1px solid #f1f5f9', background: '#fafbfc' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Fee Breakdown</div>
                      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                        <div style={{ fontSize: 13, color: '#475569' }}>Monthly: <strong style={{ color: '#1960a3' }}>₹{monthly.toLocaleString('en-IN')}</strong></div>
                        {examFee > 0 && <div style={{ fontSize: 13, color: '#475569' }}>Examination Fee: <strong style={{ color: '#7c3aed' }}>₹{examFee.toLocaleString('en-IN')}</strong></div>}
                        <div style={{ fontSize: 13, color: '#475569' }}>Annual Total: <strong style={{ color: '#1e293b' }}>₹{annualFee.toLocaleString('en-IN')}</strong></div>
                        <div style={{ fontSize: 13, color: '#475569' }}>Status: <strong style={{ color: full ? '#059669' : bal > 0 ? '#ef4444' : '#f59e0b' }}>{full ? 'Fully Paid' : bal > 0 ? 'Balance Due' : 'Pending'}</strong></div>
                      </div>
                    </div>
                  )}

                  {/* Payment History */}
                  <div style={{ padding: '16px 24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>Payment History</div>
                      <button onClick={() => { setSelClass(s.cls); setSelStu(s.id); setTab(0); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                        style={{ fontSize: 12, fontWeight: 700, padding: '6px 14px', borderRadius: 8, border: 'none', background: '#1960a3', color: '#fff', cursor: 'pointer' }}>
                        + Record Payment
                      </button>
                    </div>
                    {pays.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
                        <div style={{ fontSize: 32, marginBottom: 8 }}>🧾</div>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>No payments recorded yet</div>
                      </div>
                    ) : (
                      <div style={{ position: 'relative' }}>
                        <div style={{ position: 'absolute', left: 7, top: 0, bottom: 0, width: 2, background: '#e2e8f0' }} />
                        {pays.map(p => {
                          run -= p.amt;
                          return (
                            <div key={p.id} style={{ display: 'flex', gap: 16, marginBottom: 16, paddingLeft: 24, position: 'relative' }}>
                              <div style={{ position: 'absolute', left: 0, top: 4, width: 16, height: 16, borderRadius: '50%', background: '#10b981', border: '3px solid #fff', boxShadow: '0 0 0 2px #10b981', flexShrink: 0 }} />
                              <div style={{ flex: 1, background: '#f8fafc', borderRadius: 12, padding: '12px 16px', border: '1px solid #e8edf5' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                                  <div>
                                    <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>₹{p.amt.toLocaleString('en-IN')}</div>
                                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{p.dt} · {p.md}{p.mn ? ' · ' + p.mn : ''}</div>
                                  </div>
                                  <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: '#1960a3', background: '#dbeafe', padding: '2px 10px', borderRadius: 20 }}>{p.rc}</div>
                                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Bal: ₹{Math.max(0, run).toLocaleString('en-IN')}</div>
                                  </div>
                                </div>
                                {(p.extras || []).length > 0 && (
                                  <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #e2e8f0' }}>
                                    <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>Charges included:</div>
                                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                      {(p.extras || []).map((ex, i) => (
                                        <span key={i} style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: '#ede9fe', color: '#7c3aed' }}>
                                          {ex.label}: ₹{(parseFloat(ex.amt) || 0).toLocaleString('en-IN')}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {p.note && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>Note: {p.note}</div>}
                                {p.concession > 0 && <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 4 }}>Concession applied: ₹{p.concession.toLocaleString('en-IN')}</div>}
                                <button onClick={() => { setReceipt({ pay: p, s }); setShowRec(true); }}
                                  style={{ marginTop: 8, fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#1960a3', cursor: 'pointer' }}>
                                  View Receipt
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })() : (
              <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8', background: '#fff', borderRadius: 16, border: '1px solid #e8edf5' }}>
                <span className="material-symbols-outlined" style={{ fontSize: 48, display: 'block', marginBottom: 12 }}>account_balance_wallet</span>
                <div style={{ fontSize: 14, fontWeight: 600 }}>Select a class and student to view their fee details</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Fee Structure Tab */}
      {tab === 3 && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#1e293b' }}>Class-wise Fee Structure</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Set monthly fee and examination fee per class. These auto-fill when collecting fee.</div>
            </div>
          </div>
          {db.classes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8', background: '#fff', borderRadius: 16, border: '1px solid #e8edf5' }}>
              <span className="material-symbols-outlined" style={{ fontSize: 48, display: 'block', marginBottom: 12 }}>class</span>
              <div style={{ fontSize: 14, fontWeight: 600 }}>No classes added yet</div>
            </div>
          ) : (
            <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e8edf5', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#1960a3' }}>
                    {['Class', 'Monthly Fee (₹)', 'Examination Fee (₹)', 'Total Annual (₹)'].map(h => (
                      <th key={h} style={{ padding: '12px 20px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#fff', letterSpacing: '0.04em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {db.classes.map((cls, i) => {
                    const cf = classFeeMap[cls.name] || {};
                    const monthly = cf.monthly || 0;
                    const exam = cf.exam || 0;
                    const annual = monthly * 12 + exam;
                    return (
                      <tr key={cls.id} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                        <td style={{ padding: '14px 20px', fontWeight: 700, color: '#1e293b', fontSize: 14 }}>{cls.name}</td>
                        <td style={{ padding: '10px 20px' }}>
                          <input type="number" defaultValue={cf.monthly || ''} placeholder="0"
                            style={{ width: 120, padding: '8px 12px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: 13, outline: 'none', fontWeight: 600 }}
                            onFocus={e => e.target.style.borderColor = '#1960a3'}
                            onBlur={e => { e.target.style.borderColor = '#e2e8f0'; saveClassFee(cls.name, 'monthly', e.target.value); }} />
                        </td>
                        <td style={{ padding: '10px 20px' }}>
                          <input type="number" defaultValue={cf.exam || ''} placeholder="0"
                            style={{ width: 120, padding: '8px 12px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: 13, outline: 'none', fontWeight: 600 }}
                            onFocus={e => e.target.style.borderColor = '#1960a3'}
                            onBlur={e => { e.target.style.borderColor = '#e2e8f0'; saveClassFee(cls.name, 'exam', e.target.value); }} />
                        </td>
                        <td style={{ padding: '14px 20px', fontWeight: 700, color: '#1960a3', fontSize: 14 }}>
                          {annual > 0 ? `₹${annual.toLocaleString('en-IN')}` : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Receipt Modal */}
      {receipt && <Modal open={showRec} onClose={() => setShowRec(false)} title="Fee Receipt">
        <div style={{ fontFamily: 'Inter,sans-serif' }}>
          {/* Header */}
          <div style={{ textAlign: 'center', paddingBottom: 14, marginBottom: 14, borderBottom: '2px solid #002045' }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#002045' }}>{db.settings.school || 'LORD KRISHNA PUBLIC SCHOOL'}</div>
            {db.settings.addr?.trim() && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{db.settings.addr}</div>}
            {db.settings.phone && <div style={{ fontSize: 11, color: '#64748b' }}>📞 {db.settings.phone}</div>}
            <div style={{ fontSize: 10, fontWeight: 700, color: '#1960a3', textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: 6 }}>Fee Payment Receipt</div>
          </div>

          {/* Info grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, marginBottom: 12, border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
            {[
              ['Receipt No.', receipt.pay.rc, '#1960a3'],
              ['Date', receipt.pay.dt, null],
              ['Student', `${receipt.s.fn} ${receipt.s.ln}`, null],
              ['Adm No.', receipt.s.admno || '—', null],
              ['Class / Roll', `${receipt.s.cls} / ${receipt.s.roll || '—'}`, null],
              ["Father's Name", receipt.s.father || '—', null],
              ['Payment Method', receipt.pay.md + (receipt.pay.ref ? ` (${receipt.pay.ref})` : ''), null],
            ].map(([lbl, val, col], i) => (
              <div key={lbl} style={{ padding: '7px 10px', borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#f8fafc' : '#fff', borderRight: i % 2 === 0 ? '1px solid #f1f5f9' : 'none' }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{lbl}</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: col || '#1e293b', marginTop: 1 }}>{val}</div>
              </div>
            ))}
          </div>

          {/* Charges table */}
          {((receipt.pay.tuition > 0) || (receipt.pay.extras?.length > 0) || (receipt.pay.concession > 0) || (receipt.pay.examFee > 0) || (receipt.pay.prevBalancePaid > 0)) && (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 10, fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#f1f5f9' }}>
                  <th style={{ padding: '6px 10px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Description</th>
                  <th style={{ padding: '6px 10px', textAlign: 'right', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {receipt.pay.tuition > 0 && (
                  <tr style={{ borderBottom: '1px solid #f8fafc' }}>
                    <td style={{ padding: '6px 10px' }}>Tuition Fee{receipt.pay.mn ? ` — ${receipt.pay.mn}` : ''}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600 }}>₹{receipt.pay.tuition.toLocaleString('en-IN')}</td>
                  </tr>
                )}
                {receipt.pay.tuitionRemaining > 0 && (
                  <tr style={{ borderBottom: '1px solid #f8fafc', background: '#fff8f0' }}>
                    <td style={{ padding: '4px 10px 4px 20px', fontSize: 11, color: '#b45309' }}>↳ Tuition Remaining</td>
                    <td style={{ padding: '4px 10px', textAlign: 'right', fontSize: 11, fontWeight: 600, color: '#b45309' }}>₹{receipt.pay.tuitionRemaining.toLocaleString('en-IN')}</td>
                  </tr>
                )}
                {(receipt.pay.extras || []).map((e, i) => (
                  <React.Fragment key={i}>
                    <tr style={{ borderBottom: '1px solid #f8fafc' }}>
                      <td style={{ padding: '6px 10px' }}>{e.label}</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600 }}>₹{(parseFloat(e.amt) || 0).toLocaleString('en-IN')}</td>
                    </tr>
                    {e.remaining > 0 && (
                      <tr style={{ borderBottom: '1px solid #f8fafc', background: '#fffbeb' }}>
                        <td style={{ padding: '4px 10px 4px 20px', fontSize: 11, color: '#b45309' }}>↳ Remaining ({e.label})</td>
                        <td style={{ padding: '4px 10px', textAlign: 'right', fontSize: 11, fontWeight: 600, color: '#b45309' }}>₹{e.remaining.toLocaleString('en-IN')}</td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                {receipt.pay.extrasConcession > 0 && (
                  <tr style={{ borderBottom: '1px solid #f8fafc', color: '#b45309', background: '#fffbeb' }}>
                    <td style={{ padding: '6px 10px', fontWeight: 600 }}>Charges Concession</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600 }}>− ₹{receipt.pay.extrasConcession.toLocaleString('en-IN')}</td>
                  </tr>
                )}
                {receipt.pay.extrasRemaining > 0 && (
                  <tr style={{ borderBottom: '1px solid #f8fafc', color: '#b45309', background: '#fff8f0' }}>
                    <td style={{ padding: '6px 10px', fontWeight: 600 }}>Charges Remaining</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600 }}>₹{receipt.pay.extrasRemaining.toLocaleString('en-IN')}</td>
                  </tr>
                )}
                {receipt.pay.concession > 0 && (
                  <tr style={{ borderBottom: '1px solid #f8fafc', color: '#059669' }}>
                    <td style={{ padding: '6px 10px' }}>Tuition Concession</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600 }}>− ₹{receipt.pay.concession.toLocaleString('en-IN')}</td>
                  </tr>
                )}
                {receipt.pay.examFee > 0 && (
                  <tr style={{ borderBottom: '1px solid #f8fafc', background: '#f5f3ff' }}>
                    <td style={{ padding: '6px 10px', color: '#7c3aed', fontWeight: 600 }}>Examination Fee <span style={{ fontSize: 10, opacity: 0.7 }}>(one-time)</span></td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600, color: '#7c3aed' }}>₹{receipt.pay.examFee.toLocaleString('en-IN')}</td>
                  </tr>
                )}
                {receipt.pay.prevBalancePaid > 0 && (
                  <tr style={{ borderBottom: '1px solid #f8fafc', background: '#fff7ed' }}>
                    <td style={{ padding: '6px 10px', color: '#c2410c', fontWeight: 600 }}>Previous Balance</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600, color: '#c2410c' }}>₹{receipt.pay.prevBalancePaid.toLocaleString('en-IN')}</td>
                  </tr>
                )}
                {receipt.pay.note && (
                  <tr><td colSpan={2} style={{ padding: '6px 10px', fontSize: 10, color: '#64748b' }}>Note: {receipt.pay.note}</td></tr>
                )}
              </tbody>
            </table>
          )}

          {/* Total */}
          <div style={{ background: '#d1fae5', borderRadius: 8, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#059669' }}>Total Received</span>
            <span style={{ fontSize: 18, fontWeight: 800, color: '#059669' }}>₹{(receipt.pay.amt || 0).toLocaleString('en-IN')}</span>
          </div>

          {/* Balance due */}
          {(() => {
            const otherSrcBal = db.pays.filter(p => p.sid === receipt.pay.sid && p.id !== receipt.pay.id && (p.balanceRemaining || 0) > 0 && !p.isPrevBalancePay).reduce((sum, p) => sum + (p.balanceRemaining || 0), 0);
            const totalBalDue = (receipt.pay.balanceRemaining || 0) + otherSrcBal;
            return totalBalDue > 0 ? (
              <div style={{ background: '#fef3c7', borderRadius: 8, padding: '9px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, border: '1px solid #fcd34d' }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#92400e' }}>Balance Due</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#92400e' }}>₹{totalBalDue.toLocaleString('en-IN')}</span>
              </div>
            ) : null;
          })()}

          {/* Total remaining */}
          {((receipt.pay.tuitionRemaining || 0) + (receipt.pay.extrasRemaining || 0)) > 0 && (
            <div style={{ background: '#fff8f0', borderRadius: 8, padding: '9px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, border: '1px solid #fed7aa' }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#b45309' }}>Total Remaining to Pay</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#b45309' }}>₹{((receipt.pay.tuitionRemaining || 0) + (receipt.pay.extrasRemaining || 0)).toLocaleString('en-IN')}</span>
            </div>
          )}

          {receipt.pay.ba === 0 && (
            <div style={{ textAlign: 'center', marginTop: 10 }}>
            </div>
          )}
        </div>
        <ModalFooter><Btn onClick={() => setShowRec(false)}>Close</Btn><Btn variant="primary" onClick={printRec}>🖨 Print</Btn></ModalFooter>
      </Modal>}
    </div>
  );
}


export default Fees;
